import React from "react";
import ReactDOM from "react-dom/client";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import BugsnagPerformance from "@bugsnag/browser-performance";
import "@/index.css";
import App from "@/App";
import "@/i18n"; // Initialize i18n

// Initialize BugSnag for error reporting (before anything else)
const BUGSNAG_API_KEY = process.env.REACT_APP_BUGSNAG_API_KEY;

if (BUGSNAG_API_KEY) {
  Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    plugins: [new BugsnagPluginReact()],
    enabledReleaseStages: ['production', 'staging'],
    releaseStage: process.env.NODE_ENV || 'development',
    appVersion: '1.0.0',
    // Add user context when available
    onError: (event) => {
      // Add localStorage context for debugging
      const token = localStorage.getItem('token');
      const isImpersonating = localStorage.getItem('isImpersonating');
      if (token) {
        event.addMetadata('session', {
          hasToken: true,
          isImpersonating: isImpersonating === 'true'
        });
      }
      return true;
    }
  });
  
  BugsnagPerformance.start({ apiKey: BUGSNAG_API_KEY });
}

// Get ErrorBoundary component (or fallback if BugSnag not initialized)
// Custom fallback component for when errors occur
const ErrorFallback = ({ error, info, clearError }) => (
  <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'system-ui', maxWidth: '800px', margin: '0 auto' }}>
    <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Something went wrong</h2>
    <p style={{ color: '#666', marginBottom: '16px' }}>
      We encountered an unexpected error. Please try refreshing the page.
    </p>
    <details style={{ textAlign: 'left', background: '#1a1a1a', padding: '16px', borderRadius: '8px', marginTop: '16px', color: '#fff' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#f59e0b' }}>Error Details (click to expand)</summary>
      <pre style={{ overflow: 'auto', fontSize: '12px', marginTop: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {error?.message || error?.toString() || 'Unknown error'}
      </pre>
      {info?.componentStack && (
        <pre style={{ overflow: 'auto', fontSize: '11px', marginTop: '8px', color: '#888', whiteSpace: 'pre-wrap' }}>
          Component Stack:{info.componentStack}
        </pre>
      )}
    </details>
    <div style={{ marginTop: '20px' }}>
      <button 
        onClick={clearError} 
        style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', marginRight: '8px' }}
      >
        Try Again
      </button>
      <button 
        onClick={() => window.location.reload()} 
        style={{ padding: '8px 16px', background: '#f59e0b', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
      >
        Refresh Page
      </button>
    </div>
  </div>
);

const BugsnagErrorBoundary = BUGSNAG_API_KEY 
  ? Bugsnag.getPlugin('react').createErrorBoundary(React)
  : null;

// Wrapper component that applies the FallbackComponent prop
const ErrorBoundary = ({ children }) => {
  if (BugsnagErrorBoundary) {
    return (
      <BugsnagErrorBoundary FallbackComponent={ErrorFallback}>
        {children}
      </BugsnagErrorBoundary>
    );
  }
  return <>{children}</>;
};

// Suppress ResizeObserver benign errors (common with dialogs/popovers/charts)
// This error is harmless and occurs when resize observations are delivered after paint
const suppressResizeObserverError = () => {
  const resizeObserverErrMsg = 'ResizeObserver loop';
  
  // Suppress from window error event (capture phase for early interception)
  window.addEventListener('error', (e) => {
    if (e.message && e.message.includes(resizeObserverErrMsg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return true;
    }
  }, true);

  // Also listen in bubble phase
  window.addEventListener('error', (e) => {
    if (e.message && e.message.includes(resizeObserverErrMsg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return true;
    }
  });

  // Suppress from unhandled rejection (Promise-based)
  window.addEventListener('unhandledrejection', (e) => {
    if (e.reason && e.reason.message && e.reason.message.includes(resizeObserverErrMsg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return true;
    }
  });

  // Override console.error to suppress the error log
  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (args[0] && typeof args[0] === 'string' && args[0].includes(resizeObserverErrMsg)) {
      return;
    }
    if (args[0] instanceof Error && args[0].message && args[0].message.includes(resizeObserverErrMsg)) {
      return;
    }
    originalConsoleError.apply(console, args);
  };

  // Override window.onerror
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (message && typeof message === 'string' && message.includes(resizeObserverErrMsg)) {
      return true;
    }
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };
};

suppressResizeObserverError();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);
